import React from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { AlertTriangle, Car, Clock, ExternalLink, Camera, Upload, X } from 'lucide-react';

const EnforcementView = ({
  parkingData,
  jsonInput,
  setJsonInput,
  handleImportJSON,
  loadMockData,
  processedSpaces,
  filteredSpaces,
  stats,
  levels,
  attributes,
  searchTerm,
  setSearchTerm,
  selectedLevel,
  setSelectedLevel,
  selectedAttribute,
  setSelectedAttribute,
  violationThreshold,
  setViolationThreshold,
  citations,
  selectedSpace,
  setSelectedSpace,
  citationNotes,
  setCitationNotes,
  citationPhoto,
  handlePhotoUpload,
  handleRemovePhoto,
  handleIssueCitation,
  formatDuration,
}) => {
  if (!parkingData) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-slate-800 mb-2">Parking Enforcement</h2>
          <p className="text-slate-600">Import JSON data to view occupied parking spaces and manage citations</p>
        </div>
        
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-slate-200">
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-3">
                Paste your JSON data here:
              </label>
              <Textarea
                value={jsonInput}
                onChange={(e) => setJsonInput(e.target.value)}
                placeholder="Paste your parking JSON data here..."
                className="min-h-[200px] font-mono text-sm"
              />
            </div>
            
            <div className="flex items-center justify-center space-x-4">
              <Button 
                onClick={handleImportJSON}
                disabled={!jsonInput.trim()}
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg flex items-center space-x-2"
              >
                <Upload className="w-5 h-5" />
                <span>Import JSON Data</span>
              </Button>
              
              <div className="text-slate-400">or</div>
              
              <Button 
                onClick={loadMockData}
                variant="outline"
                className="px-8 py-3 rounded-lg border-2 border-slate-300 hover:bg-slate-50"
              >
                Load Demo Data
              </Button>
            </div>
            
            <div className="text-sm text-slate-500 text-center max-w-md mx-auto">
              Load the demo data to explore the enforcement features
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-white border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Car className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Total Vehicles</p>
                <p className="text-2xl font-bold text-slate-800">{stats.totalVehicles}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <AlertTriangle className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Violations</p>
                <p className="text-2xl font-bold text-orange-600">{stats.violations}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <Clock className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Citations</p>
                <p className="text-2xl font-bold text-red-600">{stats.citations}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl shadow-lg p-6 border border-slate-200">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="text-sm font-medium text-slate-700 mb-2 block">Search</label>
            <Input
              type="text"
              placeholder="Search plate, make, model, space..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full"
            />
          </div>
          
          <div>
            <label className="text-sm font-medium text-slate-700 mb-2 block">Level</label>
            <Select value={selectedLevel} onValueChange={setSelectedLevel}>
              <SelectTrigger>
                <SelectValue placeholder="All Levels" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Levels</SelectItem>
                {levels.map(level => (
                  <SelectItem key={level} value={level}>{level}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <label className="text-sm font-medium text-slate-700 mb-2 block">Parking Type</label>
            <Select value={selectedAttribute} onValueChange={setSelectedAttribute}>
              <SelectTrigger>
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {attributes.map(attr => (
                  <SelectItem key={attr} value={attr}>{attr}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <label className="text-sm font-medium text-slate-700 mb-2 block">
              Violation Threshold: {violationThreshold} hours
            </label>
            <Input
              type="number"
              min="1"
              max="24"
              value={violationThreshold}
              onChange={(e) => setViolationThreshold(parseInt(e.target.value) || 1)}
              className="w-full"
            />
          </div>
        </div>
      </div>

      {/* Parking Spaces Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredSpaces.map(space => (
          <Card 
            key={space.id} 
            className={`bg-white border-2 transition-all duration-300 hover:shadow-lg cursor-pointer ${
              space.isViolation ? 'border-orange-200 bg-orange-50' : 'border-slate-200'
            } ${space.isCited ? 'border-red-200 bg-red-50' : ''}`}
          >
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-bold text-slate-800">
                  {space.name}
                </CardTitle>
                <div className="flex items-center space-x-2">
                  {space.isViolation && (
                    <Badge className="bg-orange-100 text-orange-800 border-orange-200">
                      Violation
                    </Badge>
                  )}
                  {space.isCited && (
                    <Badge className="bg-red-100 text-red-800 border-red-200">
                      Cited
                    </Badge>
                  )}
                </div>
              </div>
            </CardHeader>
            
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-600">License Plate</p>
                  <p className="font-medium text-slate-800">{space.plate}</p>
                </div>
                <div>
                  <p className="text-slate-600">Level</p>
                  <p className="font-medium text-slate-800">{space.level}</p>
                </div>
                <div>
                  <p className="text-slate-600">Vehicle</p>
                  <p className="font-medium text-slate-800">{space.make} {space.model}</p>
                </div>
                <div>
                  <p className="text-slate-600">Parked For</p>
                  <p className={`font-medium ${space.isViolation ? 'text-orange-600' : 'text-slate-800'}`}>
                    {formatDuration(space.hoursParked, space.minutesParked)}
                  </p>
                </div>
              </div>
              
              <div className="text-sm">
                <p className="text-slate-600">Parking Type</p>
                <p className="font-medium text-slate-800">{space.parkingAttribute}</p>
              </div>
              
              <div className="flex items-center space-x-2 pt-2">
                <Dialog>
                  <DialogTrigger asChild>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setSelectedSpace(space)}
                      className="flex-1"
                    >
                      View Details
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-md">
                    <DialogHeader>
                      <DialogTitle>Space {space.name}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-slate-600">License Plate</p>
                          <p className="font-medium">{space.plate}</p>
                        </div>
                        <div>
                          <p className="text-slate-600">Level</p>
                          <p className="font-medium">{space.level}</p>
                        </div>
                        <div>
                          <p className="text-slate-600">Vehicle</p>
                          <p className="font-medium">{space.make} {space.model}</p>
                        </div>
                        <div>
                          <p className="text-slate-600">Parked For</p>
                          <p className="font-medium">{formatDuration(space.hoursParked, space.minutesParked)}</p>
                        </div>
                      </div>
                      
                      <div>
                        <p className="text-slate-600 text-sm">Parking Type</p>
                        <p className="font-medium">{space.parkingAttribute}</p>
                      </div>
                      
                      <div>
                        <p className="text-slate-600 text-sm">Parked Since</p>
                        <p className="font-medium">{space.startTime.toLocaleString()}</p>
                      </div>
                      
                      <div>
                        <p className="text-sm font-medium text-slate-700 mb-2">Citation Photo</p>
                        {citationPhoto ? (
                          <div className="space-y-2">
                            <div className="relative">
                              <img 
                                src={citationPhoto} 
                                alt="Citation photo" 
                                className="w-full h-32 object-cover rounded-lg border border-slate-200"
                              />
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={handleRemovePhoto}
                                className="absolute top-2 right-2 h-6 w-6 p-0"
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                            <div className="relative">
                              <input
                                type="file"
                                accept="image/*"
                                onChange={handlePhotoUpload}
                                className="absolute inset-0 opacity-0 cursor-pointer"
                              />
                              <Button variant="outline" size="sm" className="w-full flex items-center space-x-2">
                                <Camera className="w-4 h-4" />
                                <span>Replace Photo</span>
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="relative">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handlePhotoUpload}
                              className="absolute inset-0 opacity-0 cursor-pointer"
                            />
                            <Button variant="outline" size="sm" className="w-full flex items-center space-x-2">
                              <Camera className="w-4 h-4" />
                              <span>Take Photo</span>
                            </Button>
                          </div>
                        )}
                      </div>
                      
                      <div>
                        <p className="text-sm font-medium text-slate-700 mb-2">Notes</p>
                        <Textarea
                          placeholder="Add citation notes..."
                          value={citationNotes}
                          onChange={(e) => setCitationNotes(e.target.value)}
                          className="min-h-[80px]"
                        />
                      </div>
                      
                      {!space.isCited && (
                        <Button 
                          onClick={() => handleIssueCitation(space.id)}
                          className="w-full bg-red-600 hover:bg-red-700 text-white"
                        >
                          Issue Citation
                        </Button>
                      )}
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      
      {filteredSpaces.length === 0 && (
        <div className="text-center py-12">
          <p className="text-slate-600 text-lg">No occupied parking spaces found</p>
          <p className="text-slate-500 text-sm mt-2">Try adjusting your filters or importing data</p>
        </div>
      )}
    </div>
  );
};

export default EnforcementView;